import classNames from "classnames";
import { useEffect, useRef } from "react";
import FocusTrap from "focus-trap-react";

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  children: React.ReactNode;
  title?: string;
  ariaLabel?: string;
};

const Modal = ({ open, handleClose, children, title, ariaLabel }: ModalProps) => {
  const triggerRef = useRef<HTMLElement | null>(null);

  // Store reference to the element that triggered the modal
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
    }
  }, [open]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!open && triggerRef.current) {
      // Small delay to ensure modal is fully closed before restoring focus
      setTimeout(() => {
        triggerRef.current?.focus();
      }, 0);
    }
  }, [open]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, handleClose]);

  if (!open) {
    return null;
  }

  // Determine aria-label or aria-labelledby
  const ariaLabelProps = ariaLabel
    ? { "aria-label": ariaLabel }
    : title
      ? { "aria-labelledby": "modal-title" }
      : { "aria-label": "Dialog" };

  return (
    <div
      className="fixed z-[1020] overflow-y-auto top-0 w-full left-0"
      id="modal"
      onClick={() => {
        // handleClose();
      }}
    >
      <div className="flex items-center justify-center min-height-100vh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
            ​
          </span>
          <FocusTrap
            focusTrapOptions={{
              clickOutsideDeactivates: false,
              escapeDeactivates: false,
              allowOutsideClick: true,
            }}
          >
            <div
              className="inline-block align-center bg-bg-brand-secondary-default rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full"
              role="dialog"
              aria-modal="true"
              {...ariaLabelProps}
            >
              <div className="bg-bg-brand-secondary-default px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                {title && (
                  <h2 id="modal-title" className="sr-only">
                    {title}
                  </h2>
                )}
                {children}
              </div>
            </div>
          </FocusTrap>
        </div>
      </div>
    </div>
  );
};

export default Modal;
